import React, { useEffect, useRef, useCallback } from "react";
import { Container, Topbar, Button, ProgressSteps } from "@bsf/force-ui";
import { Link } from "../../router/link";
import { routes } from "../../admin/settings/routes";
import { X } from "lucide-react";
import { __ } from "@wordpress/i18n";
import Welcome from "./Welcome";
import Build from "./Build";
import Configure from "./Configure";
// import Success from "./Success";

const steps = [
    { label: "Welcome", component: Welcome },
    { label: "Configure", component: Configure },
    { label: "Create", component: Build },
];

// Step number → slug mapping.
const stepNames = { 1: 'welcome', 2: 'configure', 3: 'success' };

/**
 * Send the onboarding analytics blob to the server.
 *
 * @param {Object} data Analytics data with completed_steps, skipped_steps, etc.
 */
const saveOnboardingAnalytics = ( data ) => {
    const body = new URLSearchParams();
    body.append( 'action', 'uael_save_onboarding_analytics' );
    body.append( 'nonce', uaelSettingsData.ajax_nonce );
    body.append( 'exited_early', data.exitedEarly ? 'true' : 'false' );
    body.append( 'exit_at_step', data.exitAtStep || '' );

    ( data.completedSteps || [] ).forEach( ( s ) => body.append( 'completed_steps[]', s ) );
    ( data.skippedSteps || [] ).forEach( ( s ) => body.append( 'skipped_steps[]', s ) );

    fetch( uaelSettingsData.ajax_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    } ).catch( ( err ) => console.error( 'Onboarding analytics error:', err ) );
};

const Onboarding = () => {
    const [currentStep, setCurrentStep] = React.useState(() => {
        const savedStep = localStorage.getItem("currentStep");
        return savedStep ? parseInt(savedStep, 10) : 1;
    });

    // Track which steps were completed vs skipped across the journey.
    const stepsCompleted = useRef( JSON.parse( localStorage.getItem( 'uael_onboarding_steps_completed' ) || '[]' ) );
    const stepsSkipped   = useRef( JSON.parse( localStorage.getItem( 'uael_onboarding_steps_skipped' ) || '[]' ) );

    // Record a step as completed (localStorage only — no server call per step).
    const markStepCompleted = useCallback( ( stepName ) => {
        if ( ! stepsCompleted.current.includes( stepName ) ) {
            stepsCompleted.current.push( stepName );
            localStorage.setItem( 'uael_onboarding_steps_completed', JSON.stringify( stepsCompleted.current ) );
        }
    }, [] );

    // Record a step as skipped (localStorage only).
    const markStepSkipped = useCallback( ( stepName ) => {
        if ( ! stepsSkipped.current.includes( stepName ) ) {
            stepsSkipped.current.push( stepName );
            localStorage.setItem( 'uael_onboarding_steps_skipped', JSON.stringify( stepsSkipped.current ) );
        }
    }, [] );

    useEffect(() => {
        localStorage.setItem("currentStep", currentStep);
    }, [currentStep]);

    useEffect(() => {
        const timer = setTimeout(() => {
            localStorage.removeItem("currentStep");
        }, 180000); // 3 minutes in milliseconds.

        return () => clearTimeout(timer); // Clear the timeout if the component unmounts.
    }, []);

    // "Exit" button — send blob with early exit flag, then clean up.
    // Skip on step 3 (success) because the blob was already saved when Build mounted.
    const handleExit = () => {
        if ( currentStep >= 3 ) {
            return;
        }
        saveOnboardingAnalytics( {
            exitedEarly: true,
            exitAtStep: stepNames[ currentStep ] || '',
            completedSteps: stepsCompleted.current,
            skippedSteps: stepsSkipped.current,
        } );
        localStorage.removeItem( 'uael_onboarding_steps_completed' );
        localStorage.removeItem( 'uael_onboarding_steps_skipped' );
    };

    const StepComponent = steps[currentStep - 1]?.component;

    return (
        <div>
            <div className="w-full pb-10">
                <div className="flex flex-col items-center justify-center">
                    <Topbar className="bg-none" style={{ background: "none" }}>
                        <Topbar.Left>
                            <Topbar.Item>
                                {uaelSettingsData.uae_logo && (
                                    <Link to={routes.dashboard.path}>
                                        <img
                                            src={`${uaelSettingsData.uae_logo}`}
                                            alt="Logo"
                                            className="cursor-pointer"
                                            style={{ height: "35px" }}
                                        />
                                    </Link>
                                )}
                            </Topbar.Item>
                        </Topbar.Left>
                        <Topbar.Middle>
                            <Topbar.Item>
                                <ProgressSteps
                                    currentStep={currentStep}
									className="uae-steps"
                                    variant="number"
                                >
                                    {steps.map((step, index) => (
                                        <ProgressSteps.Step
                                            key={index}
                                            className="font-bold"
                                            labelText={step.label}
                                            size="md"
                                        />
                                    ))}
                                </ProgressSteps>
                            </Topbar.Item>
                        </Topbar.Middle>
                        <Topbar.Right>
                            <Topbar.Item>
                                <Link className="uael-remove-ring" to={routes.dashboard.path}
                                style={{ marginLeft: '125px'}}
                                onClick={handleExit}>
                                    <Button
                                        icon={<X className="size-4" />}
                                        iconPosition="right"
                                        size="xs"
                                        variant="ghost"
                                        className="uael-remove-ring"
                                    ></Button>
                                </Link>
                            </Topbar.Item>
                        </Topbar.Right>
                    </Topbar>
                </div>
            </div>

            <div className="flex items-center justify-center">
                {StepComponent && (
                    <StepComponent
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        saveOnboardingAnalytics={saveOnboardingAnalytics}
                        markStepCompleted={markStepCompleted}
                        markStepSkipped={markStepSkipped}
                        stepsCompleted={stepsCompleted}
                        stepsSkipped={stepsSkipped}
                    />
                )}
            </div>
        </div>
    );
};

export default Onboarding;
